// Single home for "which backend does this build talk to?" (BBB-457).
//
// This decision used to be made in seven places. App.js derived it properly,
// honouring REACT_APP_API_BASE_URL so a local run could point at a deployed
// backend. Five other call sites re-derived it with localhost:4000 hardcoded, so
// the override was respected on login and silently ignored by the subscription
// screens, the plan address lookup and the admin docs link — authenticating
// against one backend and then talking to another with its token.
//
// CLAUDE.md asks for a single services/api.js to replace the per-screen fetch
// calls. This is deliberately only the base-URL piece of that, not the client.


/**
 * Hostnames that mean "this front end is being served through an ngrok tunnel".
 *
 * Matched against the HOSTNAME and only as a whole trailing label, never against
 * the full href. `href.includes('ngrok')` was the original test and it was wrong
 * in a way that mattered: a link to
 * `https://<the real production host>/?ref=ngrok` pointed a production build at
 * the staging backend, so a crafted link could have sent a farmer's session
 * somewhere it did not belong.
 */
const NGROK_HOST_SUFFIXES = ['.ngrok.io', '.ngrok-free.app', '.ngrok.app', '.ngrok.dev'];

function isNgrokHost(hostname) {
  if (typeof hostname !== 'string' || !hostname) return false;
  const host = hostname.toLowerCase();
  return NGROK_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
}

/** Port bbb-server listens on locally. */
const DEV_API_PORT = 4000;

/**
 * The backend origin this build should use.
 *
 * `location` is a parameter so the mapping can be asserted without a browser;
 * app callers pass nothing and get window.location.
 *
 * Development falls back to the host serving the front end rather than the
 * literal string "localhost", so opening the dev server from a phone on the
 * same network reaches the developer's machine instead of the phone itself.
 *
 * @param {{href?: string, hostname?: string}} [location] defaults to window.location
 * @returns {string|undefined} the origin, or undefined in a production build
 *   with REACT_APP_API_BASE_URL unset
 */
export function resolveApiBaseUrl(
  location = typeof window === 'undefined' ? undefined : window.location,
) {
  // A tunnelled front end cannot reach the developer's own localhost, so the
  // one thing this must not do is fall through to the dev default below. It
  // returns the explicit override and nothing else.
  //
  // It used to return a hardcoded staging origin instead, which was two
  // problems. An environment-specific endpoint was pinned in source, and it
  // took precedence over REACT_APP_API_BASE_URL, so a developer who set the
  // override was silently ignored inside a tunnel. Now the override is the only
  // answer, and a developer who has not set one gets undefined and a request
  // that visibly fails, rather than a session quietly pointed at staging.
  if (isNgrokHost(location?.hostname)) return process.env.REACT_APP_API_BASE_URL;

  if (process.env.NODE_ENV === 'development') {
    // Unset (the team default) keeps the local backend.
    if (process.env.REACT_APP_API_BASE_URL) return process.env.REACT_APP_API_BASE_URL;
    return `http://${location?.hostname ?? 'localhost'}:${DEV_API_PORT}`;
  }

  return process.env.REACT_APP_API_BASE_URL;
}

export default resolveApiBaseUrl;
